<script setup lang="ts">
import { inject, ref, toRefs, watch } from 'vue';
import type { Lot, LotCountNeighborhood } from './../types';
import { isMobile, isTablet } from 'mobile-device-detect';
import { ModalType, Status } from '@/constant';
import SideBarPlanListItem from './SideBarPlanListItem.vue';
import lodash from 'lodash';

interface Props {
  selectedJobNum: string;
  qmiAndHomeplans: any[];
  selectedHomePlanIds: string[];
  selectedLotId: string;
  selectedHomePlanId: string;
  selectedLotStatus: string;
  availableLots: number;
  isKioskMode: boolean;
  neighborhoodId: string;
  listPlanId: string[];
  listHomeType: string[];
  selectedNeighborhoodIds: string[];
  initialHomePlanCount: number;
  lotsCount: LotCountNeighborhood[];
}

const props = defineProps<Props>();

const {
  selectedJobNum,
  qmiAndHomeplans,
  selectedHomePlanIds,
  selectedLotId,
  selectedHomePlanId,
  selectedLotStatus,
  availableLots,
  isKioskMode,
  neighborhoodId,
  listHomeType,
  selectedNeighborhoodIds,
  initialHomePlanCount,
  lotsCount,
} = toRefs(props);

const emit = defineEmits([
  'setHomePlanId',
  'cleanHomeId',
  'toggleShowFilters',
  'openModal',
  'closeModal',
]);

// injector
const isSalesOrLandView = inject('setSelectionDropdown', ref(false));

const hasMobile = !!(isMobile && !isTablet);

let planClicks = {};
const previousSelectedPlan = ref('');
const availableStatus = Status;
const plansToList = ref<any[]>([]);

const generateKey = (homePlan: any, index: number) =>
  `${homePlan.plan_code || 'no-plan-code'}-${
    homePlan.plan_id || 'no-plan-id'
  }-${homePlan.future_id || 'no-future-id'}-${index}`;

const QmiplanClickAction = (homePlan: any) => {
  if (hasMobile) {
    emit('toggleShowFilters');
  }
  homePlan.price >= 0
    ? planQmiClickAction(homePlan.job_number)
    : planClickAction(homePlan);
};

const planClickAction = (homePlan: any) => {
  const planId = homePlan.plan_id;
  if (previousSelectedPlan.value !== planId) {
    delete planClicks[planId];
  }
  previousSelectedPlan.value = planId;

  if (!planClicks[planId]) {
    planClicks[planId] = 0;
  }
  planClicks[planId]++;

  if (planClicks[planId] % 2) {
    emit('setHomePlanId', { planId, jobNum: '' });
    if (isSalesOrLandView.value)
      emit('openModal', { type: ModalType.PLAN_DETAILS, plan: homePlan });
  } else {
    emit('cleanHomeId');
  }
};

const planQmiClickAction = (jobNum: string) => {
  if (previousSelectedPlan.value !== jobNum) {
    delete planClicks[jobNum];
  }
  previousSelectedPlan.value = jobNum;

  if (!planClicks[jobNum]) {
    planClicks[jobNum] = 0;
  }
  planClicks[jobNum]++;

  if (planClicks[jobNum] % 2) emit('setHomePlanId', { planId: '', jobNum });
  else emit('cleanHomeId');
  emit('closeModal');
};

const checkSelected = (homePlan: any) => {
  const isNotSelectedFromSidebar =
    !selectedHomePlanId.value && !selectedJobNum.value;
  return isNotSelectedFromSidebar
    ? false
    : (selectedHomePlanId.value === homePlan.plan_id &&
        selectedJobNum.value.length <= 0) ||
        (lodash.includes(selectedHomePlanIds, homePlan.plan_id) &&
          !!selectedLotId.value &&
          selectedLotStatus.value === availableStatus.QUICK_MOVE_IN) ||
        selectedJobNum.value === homePlan.job_number;
};

watch(selectedLotId, (newVal) => {
  if (newVal) {
    planClicks = {};
  }
});

const updatePlans = () => {
  if (!isSalesOrLandView.value) {
    plansToList.value = qmiAndHomeplans.value;
    return;
  }
  const hasSelectedPlans = selectedHomePlanIds.value.length > 0;

  plansToList.value = hasSelectedPlans
    ? qmiAndHomeplans.value.filter(
        (plan) =>
          selectedHomePlanIds.value.includes(plan.plan_id) ||
          selectedLotId.value.includes(plan.job_number)
      )
    : qmiAndHomeplans.value;
};
watch([selectedHomePlanIds, qmiAndHomeplans], updatePlans, {
  immediate: true,
});

const voidHomePlanId = () => {
  previousSelectedPlan.value = '';
  emit('cleanHomeId');
};
</script>

<template>
  <div class="sidebar-wrapper scrollbar">
    <div class="sideplan-list-wrapper">
      <div
        v-for="(homePlan, index) in plansToList"
        :key="generateKey(homePlan, index)"
        :class="[
          'sideplan-list emb-card',
          {
            'is-selected': checkSelected(homePlan),
          },
        ]"
        @click="QmiplanClickAction(homePlan)"
      >
        <SideBarPlanListItem
          :homePlan="homePlan"
          :qmiAndHomeplans="plansToList"
          :selectedLotId="selectedLotId"
          :isKioskMode="isKioskMode"
          :neighborhoodId="neighborhoodId"
          :selectedNeighborhoodIds="selectedNeighborhoodIds"
          :lotsCount="lotsCount"
          @setHomePlanId="$emit('setHomePlanId', $event)"
          @CleanHomeId="$emit('cleanHomeId')"
          @toggleshowFilters="$emit('toggleShowFilters')"
          @voidHomePlanId="voidHomePlanId()"
        />
      </div>

      <h4
        class="no-results"
        v-if="!qmiAndHomeplans.length && initialHomePlanCount"
      >
        No Results Available
      </h4>
      <h4 class="no-results" v-if="!initialHomePlanCount">
        Home plans coming soon
      </h4>
    </div>
  </div>
</template>

<style lang="scss">
.is-slider {
  height: 100%;
  padding-left: 12px;
  padding-right: 15px;
}

.simplebar-scrollbar:before {
  background: $light-gray !important;
}

.simplebar-track.simplebar-vertical {
  width: 8px;
}
.sideplan-list {
  &-wrapper {
    display: flex;
    flex-direction: column;
    row-gap: 10px;
  }

  display: flex;
  flex-direction: column;
  row-gap: 10px;
  background-color: var(--cardBg, $white);
  min-height: 217px;
  border: 3px solid var(--cardBgBdr, transparent);
  cursor: pointer;
  border-radius: 4px;

  &.is-selected {
    --cardBg: var(--commCardBgSelected);
    --cardBgBdr: #{$lime-green-new};
    box-shadow: 0px -2px 7px #434805;
  }

  &-title {
    @include font(12px, 700);
    text-decoration-line: underline;
    color: $gray-600;
  }

  &-img {
    position: relative;
    cursor: pointer;

    .map-card-imgbanner {
      img {
        --mapCardImgHeight: 130px;
      }
    }

    &.is-selected {
      border: 3px solid $primary;
    }
  }

  .qmi {
    border-radius: 4px;
    background: #99b5c5;
    font-size: 10px;
    font-weight: 600;
    line-height: 14px;
    padding: 1px 10px;
    min-width: unset;
    margin-left: auto;
  }
}

.no-results {
  text-align: center;
  color: $white;
  @include font(14px, 400);
  text-transform: capitalize;
  margin-top: 60px;
}

.removeUnderline {
  text-decoration: none;
}
</style>
